import { Add } from '@material-ui/icons';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useRef } from 'react';
import { AvatarEditorProps } from 'react-avatar-editor';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ItemCreationToolTip, ItemCreationToolTipProps } from '~/Components';
import { Pen } from '~/Components/Icons/Pen';
import { ImageUpload } from '~/Components/ImageUpload';
import { ImageType } from '~/Models/DTO/Location.models';
import { Typography, Button, ImageEditorModal } from '~/UIKit';
import { ImageUploaderStyles } from './ImageUploader.styles';
import { IImageStyle, useImageUploader } from './useImageUploader';

interface ImageUploaderProps {
  title?: string;
  description?: string;
  images: ImageType[];
  imageStyle?: IImageStyle;
  maxImageNumber?: number;
  canvasStyle?: Partial<AvatarEditorProps>;
  onDragDrop?: (from: number, to: number) => void;
  onEditImage: (imageSrc: string, imageOrder: number) => void;
  onNewImage: (imageSrc: string) => void;
  onDeleteImage: (imageIndex: number) => void;
  tooltip?: Omit<ItemCreationToolTipProps, 'containerRef'>;
}

const defaultCanvasStyle = {
  width: 300,
  height: 300,
  color: [0, 0, 0, 0.4],
  borderRadius: 500,
  border: 0,
};

export const ImageUploader = ({
  title,
  description,
  images,
  imageStyle = {},
  tooltip,
  maxImageNumber = 12,
  canvasStyle = defaultCanvasStyle,
  onDragDrop,
  onEditImage,
  onNewImage,
  onDeleteImage,
}: ImageUploaderProps) => {
  const { classes } = ImageUploaderStyles(imageStyle)();
  const {
    setEditImageIndex,
    editImageSrc,
    editImageIndex,
    handleDragStart,
    handleImageEdit,
    isEditLoading,
    handleDragOver,
    handleDrop,
    handleImageDelete,
    handleNewImage,
    handleEditMode,
  } = useImageUploader(images, onEditImage, onDragDrop, onNewImage, onDeleteImage);
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={classes.imageUploaderWrapper} ref={containerRef}>
      {title && (
        <div className={classes.header}>
          <Typography className={classes.title}>
            {title} {tooltip && <ItemCreationToolTip {...tooltip} containerRef={containerRef} />}
          </Typography>
          {description && <Typography>{description}</Typography>}
        </div>
      )}
      <DndProvider backend={HTML5Backend}>
        <div className={classes.imageContainer}>
          {_.sortBy(images, ['order']).map(
            (image, index) =>
              image.imageUrl && (
                <div
                  className={classes.imageWrapper}
                  data-order={image.order}
                  onDragOver={handleDragOver}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  draggable
                  key={`${image.imageOriginalUrl}-${index}`}
                >
                  <img id={`image-${image.order}`} className={classes.image} src={image.imageUrl} />
                  <Button
                    color="primary"
                    variant="outlined"
                    component="span"
                    onClick={() => handleEditMode(image)}
                    className={classes.editButton}
                  >
                    <Pen className={classes.editButtonIcon} />
                  </Button>
                </div>
              ),
          )}
          {images.length < maxImageNumber && (
            <div className={classNames(classes.addImageArea, classes.image)}>
              <ImageUpload
                className={classes.uploadButton}
                actionButtonIcon={<Add />}
                canvasStyle={canvasStyle}
                onUpload={handleNewImage}
                restrictedToUSCTypes
              />
            </div>
          )}
          {editImageIndex !== undefined && images?.length > 0 && images[editImageIndex] && (
            <ImageEditorModal
              imageSrc={editImageSrc}
              onUpload={handleImageEdit}
              onDelete={handleImageDelete}
              onClose={() => {
                setEditImageIndex(undefined);
              }}
              isLoading={isEditLoading}
              canvasStyle={canvasStyle}
              open={editImageIndex !== undefined}
              primaryButtonLabel={t('app.save')}
            />
          )}
        </div>
      </DndProvider>
    </div>
  );
};
